import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, Flame, Star, Trophy } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { useFeatureAccess } from '../../contexts/FeatureAccessContext';
import { useVocabularyJourneyData } from '../../modules/student-dashboard/hooks/useVocabularyJourneyData';
import { ROUTES } from '../../utils/constants';

function JourneySkeleton() {
  return (
    <Card className="border-indigo-100/80 dark:border-app-primary/30/50 bg-gradient-to-br from-white via-indigo-50/30 to-violet-50/40 dark:from-gray-900 dark:via-violet-950/40 dark:to-gray-950">
      <CardContent className="p-5 space-y-4 animate-pulse">
        <div className="h-5 w-48 bg-slate-200 dark:bg-gray-700 rounded" />
        <div className="grid grid-cols-2 gap-3">
          <div className="h-12 bg-slate-100 dark:bg-gray-800 rounded" />
          <div className="h-12 bg-slate-100 dark:bg-gray-800 rounded" />
        </div>
        <div className="h-3 bg-slate-100 dark:bg-gray-800 rounded-full" />
        <div className="h-9 bg-slate-100 dark:bg-gray-800 rounded-lg" />
      </CardContent>
    </Card>
  );
}

export function MyVocabularyJourneyPanel() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { journey: data, totalStars, loading, gradeError } = useVocabularyJourneyData();

  if (!isSchoolFeatureEnabled('vocabulary')) return null;
  if (loading) return <JourneySkeleton />;

  const total = data.total_assigned_words || 0;
  const mastered = data.words_mastered || 0;
  const progressPct = total > 0 ? Math.min(100, Math.round((mastered / total) * 100)) : 0;

  const onContinue = () => {
    const target = data.continue_practice;
    if (!target?.lesson_no) {
      navigate(ROUTES.STUDENT_VOCABULARY);
      return;
    }
    const base = `${ROUTES.STUDENT_VOCABULARY}/lesson/${target.lesson_no}`;
    const qs = target.vocabulary_entry_id ? `?entry=${encodeURIComponent(target.vocabulary_entry_id)}` : '';
    if (target.mode === 'review') {
      navigate(`${ROUTES.STUDENT_VOCABULARY_FLASHCARDS}/lesson/${target.lesson_no}`);
      return;
    }
    navigate(`${base}${qs}`);
  };

  return (
    <Card className="overflow-hidden border-indigo-100/80 dark:border-violet-700/50 bg-gradient-to-br from-white via-indigo-50/30 to-violet-50/40 dark:from-gray-900 dark:via-violet-950/50 dark:to-gray-950 shadow-sm dark:shadow-violet-950/20">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-app-primary dark:text-violet-400" aria-hidden />
          <h3 className="font-semibold text-slate-900 dark:text-gray-100">
            {t('dashboard.studentHome.vocab.title')}
          </h3>
        </div>

        {gradeError ? (
          <p className="text-sm text-amber-700 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
            {t('dashboard.studentHome.vocab.gradeNotSet')}
          </p>
        ) : null}

        <div className="rounded-xl border-2 border-amber-300/70 dark:border-amber-600/40 bg-gradient-to-r from-amber-50 via-yellow-50/80 to-orange-50/60 dark:from-amber-950/30 dark:via-yellow-950/20 dark:to-orange-950/20 px-4 py-3 flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/90 dark:bg-amber-900/40 shadow-sm ring-2 ring-amber-200/80 dark:ring-amber-700/50">
            <Star className="w-6 h-6 text-amber-500 fill-amber-400" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-800/90 dark:text-amber-200/90">
              {t('gamification.totalStars.title')}
            </p>
            <p className="text-2xl font-black text-amber-700 dark:text-amber-300">{totalStars}</p>
            <p className="text-[11px] text-amber-900/70 dark:text-amber-200/70">{t('gamification.totalStars.hint')}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <div className="flex items-center gap-2 rounded-xl bg-white/80 dark:bg-gray-800/90 px-3 py-2 border border-indigo-100 dark:border-app-primary/30/60">
            <Star className="w-4 h-4 text-amber-500 dark:text-amber-400 shrink-0" aria-hidden />
            <div>
              <p className="font-bold text-app-primary dark:text-violet-300">{data.words_learned}</p>
              <p className="text-xs text-slate-500 dark:text-gray-400">
                {t('dashboard.studentHome.vocab.wordsLearned')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl bg-white/80 dark:bg-gray-800/90 px-3 py-2 border border-indigo-100 dark:border-app-primary/30/60">
            <Trophy className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" aria-hidden />
            <div>
              <p className="font-bold text-emerald-700 dark:text-emerald-300">{data.vocabulary_score}%</p>
              <p className="text-xs text-slate-500 dark:text-gray-400">
                {t('dashboard.studentHome.vocab.vocabularyScore')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-xl bg-white/80 dark:bg-gray-800/90 px-3 py-2 border border-indigo-100 dark:border-app-primary/30/60">
            <Flame className="w-4 h-4 text-orange-500 dark:text-orange-400 shrink-0" aria-hidden />
            <div>
              <p className="font-bold text-orange-600 dark:text-orange-300">{data.practice_streak}</p>
              <p className="text-xs text-slate-500 dark:text-gray-400">
                {t('dashboard.studentHome.vocab.dayStreak')}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="h-2.5 rounded-full bg-slate-200/80 dark:bg-gray-700/80 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 dark:from-app-primary dark:to-app-primary/75 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="text-xs text-slate-600 dark:text-gray-400 text-center">
            {t('dashboard.studentHome.vocab.wordsMastered', { mastered, total })}
          </p>
        </div>

        {data.recently_learned.length > 0 ? (
          <div className="text-sm">
            <p className="text-xs font-medium text-slate-500 dark:text-gray-400 mb-1">
              {t('dashboard.studentHome.vocab.recentlyLearned')}
            </p>
            <p className="tamil-text text-app-primary dark:text-violet-200 font-medium">
              {data.recently_learned.join(' • ')}
            </p>
          </div>
        ) : total === 0 ? (
          <p className="text-xs text-slate-500 dark:text-gray-400 text-center">
            {t('dashboard.studentHome.vocab.emptyVocab')}
          </p>
        ) : null}

        <Button className="w-full" onClick={onContinue}>
          {t('dashboard.studentHome.vocab.continuePractice')}
        </Button>
      </CardContent>
    </Card>
  );
}
